import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiama } from './cliente.js';
import type { Ordine } from './ordini.js';

/**
 * Scrivere un ordine: crearlo, aggiungere righe, correggerle.
 *
 * Il prezzo non lo decide il client: lo chiede al servizio, che sa quale
 * listino vale e se per la destinazione dell'ordine esiste un prezzo
 * dedicato. Calcolarlo qui vorrebbe dire riscrivere quella regola una seconda
 * volta, e le due copie diventerebbero diverse.
 */

export interface Destinazione {
  id: number;
  label: string;
  address: string | null;
  addressExtra: string | null;
  postalCode: string | null;
  city: string | null;
  provinceState: string | null;
  country: string | null;
  /** Il codice con cui parte verso BC: quello della destinazione, o il suo numero. */
  bcCode: string;
  isMain: boolean;
  /** Le Business Central in cui c'è: `['SPA']`, `['INC']`, tutte e due o nessuna. */
  bc: string[];
}

export interface ListinoDisponibile {
  id: number;
  name: string;
  startDate: string | null;
  endDate: string | null;
  flatDiscountPercentage: string | null;
  /** In vigore alla data dell'ordine, non a oggi. */
  current: boolean;
}

/** Un'aliquota dell'azienda dell'ordine, per le righe senza listino. */
export interface AliquotaOrdine {
  id: number;
  label: string;
  isDefault: boolean;
}

export interface PrezzoProposto {
  pricelistId: number;
  pricelistName: string;
  priceExw: string;
  costDdp: string;
  taxId: number;
  discountPercentage1: string | null;
  discountPercentage2: string | null;
  discountPercentage3: string | null;
  crossReference: string | null;
  /** Vero quando il prezzo viene da un'eccezione per questa destinazione. */
  fromDestination: boolean;
}

export function useDestinazioniCliente(clienteId: number | null) {
  return useQuery({
    queryKey: ['destinazioni-cliente', clienteId],
    queryFn: () =>
      chiama<{ destinations: Destinazione[] }>(`/api/customers/${clienteId}/destinations`),
    enabled: clienteId !== null && clienteId > 0,
  });
}

export function useCreaOrdine() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<{ id: number }>('/api/orders', { metodo: 'POST', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ordini'] });
      void client.invalidateQueries({ queryKey: ['ordini-conteggi'] });
    },
  });
}

export function useListiniOrdine(ordineId: number, attivo: boolean) {
  return useQuery({
    queryKey: ['listini-ordine', ordineId],
    queryFn: () =>
      chiama<{ pricelists: ListinoDisponibile[]; taxes: AliquotaOrdine[] }>(
        `/api/orders/${ordineId}/pricelists`,
      ),
    enabled: attivo,
    staleTime: 5 * 60_000,
  });
}

/** Che prezzo propone quel listino per quell'articolo, su questo ordine. */
export function usePrezzoProposto(
  ordineId: number,
  pricelistId: number | null,
  articleId: number | null,
) {
  return useQuery({
    queryKey: ['prezzo-proposto', ordineId, pricelistId, articleId],
    queryFn: () =>
      chiama<{ price: PrezzoProposto | null }>(
        `/api/orders/${ordineId}/price?pricelistId=${pricelistId}&articleId=${articleId}`,
      ),
    enabled: pricelistId !== null && articleId !== null,
  });
}

function usaRighe(ordineId: number) {
  const client = useQueryClient();
  return (aggiornato: Ordine) => {
    client.setQueryData(['ordine', ordineId], aggiornato);
    void client.invalidateQueries({ queryKey: ['ordini'] });
    // avanzare richiede almeno una riga: le strade possibili cambiano
    void client.invalidateQueries({ queryKey: ['transizioni', ordineId] });
  };
}

export function useAggiungiRigaOrdine(ordineId: number) {
  const dopo = usaRighe(ordineId);
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<Ordine>(`/api/orders/${ordineId}/lines`, { metodo: 'POST', corpo: dati }),
    onSuccess: dopo,
  });
}

export function useModificaRigaOrdine(ordineId: number) {
  const dopo = usaRighe(ordineId);
  return useMutation({
    mutationFn: ({ lineId, ...dati }: Record<string, unknown> & { lineId: number }) =>
      chiama<Ordine>(`/api/orders/${ordineId}/lines/${lineId}`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: dopo,
  });
}

export function useTogliRigaOrdine(ordineId: number) {
  const dopo = usaRighe(ordineId);
  return useMutation({
    mutationFn: (lineId: number) =>
      chiama<Ordine>(`/api/orders/${ordineId}/lines/${lineId}`, { metodo: 'DELETE' }),
    onSuccess: dopo,
  });
}
